import type { Counter, MinigameEngine, TownId } from '@wyn/content';
import { awardDeeds } from './deeds.ts';
import { advanceFirstSteps } from './firstSteps.ts';
import { featKey } from './minigames/feats.ts';
import { advanceNoticeBoard } from './noticeBoard.ts';
import type { GameEvent, LedgerDelta, PlayerState } from './player.ts';

/** Plays and best score for one activity. */
export interface GameRecord {
  plays: number;
  best: number;
}

/**
 * What a player has done, kept on their row. It can't be rebuilt from the Chronicle (which expires),
 * so it starts empty for players from before it existed. Missing means nothing yet.
 */
export interface PlayerRecord {
  counts: Partial<Record<Counter, number>>;
  /** By `gameKey(venue, activity)`. */
  games: Record<string, GameRecord>;
  /** How often each feat was done, by `featKey(engine, feat)`. */
  feats: Record<string, number>;
  /** Every town set foot in, home first. */
  towns: TownId[];
  /** When each deed was done (epoch ms), by key. Records from before deeds have no field. */
  deeds: Record<string, number>;
}

export const gameKey = (venue: string, activity: string) => `${venue}/${activity}`;

export const emptyRecord = (): PlayerRecord => ({ counts: {}, games: {}, feats: {}, towns: [], deeds: {} });

/** A player's record, or an empty one, with any field it was saved without. */
export const recordOf = (p: Pick<PlayerState, 'record'>): PlayerRecord => ({ ...emptyRecord(), ...p.record });

/**
 * The staked games whose winnings count as `tableCoin`. The Butts take an entry fee too, but archery is
 * real time: the client holds its seed, so its prizes can't feed a deed.
 */
const TABLE_ENGINES: readonly MinigameEngine[] = ['dice', 'cards'];

/** The best score at an activity before now, or null if never played (or not since records began). */
export const bestAt = (p: Pick<PlayerState, 'record'>, venue: string, activity: string): number | null =>
  p.record?.games[gameKey(venue, activity)]?.best ?? null;

/** What `settleMinigame` puts in the `minigame` event's data, for the record to read. */
export interface MinigameEventData {
  venue: string;
  activity: string;
  engine: MinigameEngine;
  score: number;
  xp: number;
  feats: string[];
  /** Coin paid out, and the stake paid to sit down. */
  coin: number;
  stake: number;
  masterwork: boolean;
}

/** The player with their record brought up to date, and what any deeds done paid: `deed` events and ledger rows. */
export interface ProgressMade {
  player: PlayerState;
  events: GameEvent[];
  ledger: LedgerDelta[];
}

/**
 * Bring a player's record up to date with what an action did, award any deed it completes, and advance
 * the first-steps chain (`advanceFirstSteps`) and the day's notice board (`advanceNoticeBoard`). Pure, and read only from the action's own events and the
 * row itself, so it runs inside the action's transaction with no rows of its own (as mastery does). Whoever commits a player's action calls it
 * once, before responding, and commits its events and ledger rows with the action's own.
 */
export function applyProgress(player: PlayerState, events: readonly GameEvent[], now: number): ProgressMade {
  const before = recordOf(player);
  const r: PlayerRecord = { counts: { ...before.counts }, games: { ...before.games }, feats: { ...before.feats }, towns: [...before.towns], deeds: { ...before.deeds } };
  const add = (c: Counter, n = 1) => {
    if (n) r.counts[c] = (r.counts[c] ?? 0) + n;
  };

  for (const e of events) {
    const d = e.data ?? {};
    switch (e.type) {
      case 'minigame': {
        const m = d as unknown as MinigameEventData;
        if (!m.engine) break;
        add('games');
        const k = gameKey(m.venue, m.activity);
        const g = r.games[k];
        r.games[k] = { plays: (g?.plays ?? 0) + 1, best: Math.max(g?.best ?? 0, m.score) };
        for (const f of m.feats ?? []) {
          const fk = featKey(m.engine, f);
          r.feats[fk] = (r.feats[fk] ?? 0) + 1;
          add('feats');
        }
        if (m.masterwork) add('masterworks');
        if (m.stake > 0 && TABLE_ENGINES.includes(m.engine)) add('tableCoin', Math.max(0, m.coin - m.stake));
        break;
      }
      case 'recipe':
        if (d.quality === 'masterwork') add('masterworks');
        break;
      case 'fight': {
        const won = d.result === 'won' ? d.role === 'attacker' : d.result === 'lost' ? d.role === 'defender' : null;
        if (won === null) break;
        add(won ? 'fightsWon' : 'fightsLost');
        if (won && d.role === 'attacker') {
          if (d.choice === 'spare') add('spares');
          if (d.choice === 'rob') add('robs');
          if (d.choice === 'maim') add('maims');
        }
        break;
      }
      case 'trade':
      case 'market-bought':
      case 'market-sold':
        add('trades');
        break;
      default:
        if (e.type.startsWith('crime-')) {
          add('crimes');
          if (e.type === 'crime-caught') add('crimesCaught');
        }
    }
  }

  // Towns are read off the row, not the events: an arrival can land with any action.
  if (!r.towns.includes(player.homeTown)) r.towns.unshift(player.homeTown);
  if (!r.towns.includes(player.town)) r.towns.push(player.town);

  const deeds = awardDeeds(r, now);
  const steps = advanceFirstSteps({ ...player, renown: player.renown + deeds.renown, record: r }, events, now);
  const board = advanceNoticeBoard(steps.player, events, now);
  return { player: board.player, events: [...deeds.events, ...steps.events, ...board.events], ledger: [...deeds.ledger, ...steps.ledger] };
}

/** The other result of an action, with the progress it made folded in: what a commit takes. */
export function withProgress<R extends { player: PlayerState; events: GameEvent[]; ledger: LedgerDelta[] }>(result: R, earlier: readonly GameEvent[], now: number): R {
  const p = applyProgress(result.player, [...earlier, ...result.events], now);
  return { ...result, player: p.player, events: [...result.events, ...p.events], ledger: [...result.ledger, ...p.ledger] };
}
